import { useState, useEffect, useCallback } from "react";

const API = process.env.REACT_APP_API_URL || "http://localhost:4000";

const CATEGORIES = [
  { value: "billing",   label: "Billing & Payments",  icon: "◈" },
  { value: "technical", label: "Technical Issue",      icon: "⊞" },
  { value: "account",   label: "Account & Access",     icon: "◉" },
  { value: "general",   label: "General Question",     icon: "△" },
];

const PRIORITIES = [
  { value: "low",    label: "Low",    color: "var(--text-muted)" },
  { value: "normal", label: "Normal", color: "var(--text-muted)" },
  { value: "high",   label: "High",   color: "#D4AF37" },
  { value: "urgent", label: "Urgent", color: "#e07070" },
];

const STATUS_META = {
  open:        { label: "Open",        bg: "rgba(212,175,55,0.12)", color: "#D4AF37",  border: "rgba(212,175,55,0.3)" },
  in_progress: { label: "In Progress", bg: "rgba(100,160,220,0.12)", color: "#78b4e8",  border: "rgba(100,160,220,0.3)" },
  resolved:    { label: "Resolved",    bg: "rgba(100,200,130,0.12)", color: "#52D68A",  border: "rgba(100,200,130,0.3)" },
  closed:      { label: "Closed",      bg: "rgba(120,120,120,0.1)",  color: "var(--text-muted)",     border: "rgba(120,120,120,0.25)" },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.open;
  return (
    <span style={{
      padding: "3px 10px", borderRadius: "20px", fontSize: "11px",
      fontWeight: "600", letterSpacing: "0.04em",
      background: m.bg, color: m.color, border: `1px solid ${m.border}`,
      fontFamily: "'JetBrains Mono', monospace",
    }}>{m.label.toUpperCase()}</span>
  );
}

function PriorityDot({ priority }) {
  const p = PRIORITIES.find(x => x.value === priority) || PRIORITIES[1];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: "5px",
      fontSize: "12px", color: p.color,
    }}>
      <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: p.color, display: "inline-block" }} />
      {p.label}
    </span>
  );
}

function TicketCard({ ticket, onExpand, expanded }) {
  const cat = CATEGORIES.find(c => c.value === ticket.category);
  return (
    <div
      onClick={onExpand}
      style={{
        background: "var(--bg-main)", border: `1px solid ${expanded ? "#243424" : "var(--border)"}`,
        borderRadius: "14px", padding: "20px 24px",
        cursor: "pointer", transition: "all 0.18s",
        marginBottom: "10px",
      }}
      onMouseEnter={e => { if (!expanded) e.currentTarget.style.borderColor = "#1E2C1E"; }}
      onMouseLeave={e => { if (!expanded) e.currentTarget.style.borderColor = "var(--border)"; }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px", flexWrap: "wrap" }}>
            <span style={{ fontSize: "11px", color: "#D4AF37", fontFamily: "'JetBrains Mono', monospace" }}>
              #{ticket.id}
            </span>
            <span style={{ fontSize: "11px", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace" }}>
              {cat?.icon} {cat?.label || ticket.category}
            </span>
            <PriorityDot priority={ticket.priority} />
          </div>
          <div style={{ fontSize: "15px", fontWeight: "600", color: "var(--text-primary)", letterSpacing: "-0.01em", marginBottom: "4px" }}>
            {ticket.subject}
          </div>
          <div style={{ fontSize: "12px", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace" }}>
            {new Date(ticket.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
          </div>
        </div>
        <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: "12px" }}>
          <StatusBadge status={ticket.status} />
          <span style={{ color: "var(--text-faint)", fontSize: "13px", transition: "transform 0.2s", display: "inline-block", transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}>▾</span>
        </div>
      </div>

      {expanded && (
        <div style={{ marginTop: "18px", paddingTop: "18px", borderTop: "1px solid var(--border)" }}>
          <div style={{ fontSize: "13px", color: "var(--text-secondary)", lineHeight: "1.7", whiteSpace: "pre-wrap" }}>
            {ticket.description}
          </div>
          {ticket.admin_notes && (
            <div style={{
              marginTop: "16px", padding: "14px 16px",
              background: "rgba(212,175,55,0.06)", borderRadius: "10px",
              border: "1px solid rgba(212,175,55,0.15)",
            }}>
              <div style={{ fontSize: "10px", color: "#D4AF37", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", marginBottom: "6px" }}>
                ◈ RESPONSE FROM SUPPORT
              </div>
              <div style={{ fontSize: "13px", color: "#d0c8b8", lineHeight: "1.6" }}>
                {ticket.admin_notes}
              </div>
            </div>
          )}
          {ticket.updated_at !== ticket.created_at && (
            <div style={{ marginTop: "10px", fontSize: "11px", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace" }}>
              Last updated {new Date(ticket.updated_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SupportPage({ token }) {
  const [tickets,    setTickets]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm,   setShowForm]   = useState(false);
  const [expanded,   setExpanded]   = useState(null);
  const [submitOk,   setSubmitOk]   = useState(false);
  const [error,      setError]      = useState("");

  // Form state
  const [subject,     setSubject]     = useState("");
  const [category,    setCategory]    = useState("general");
  const [description, setDescription] = useState("");
  const [priority,    setPriority]    = useState("normal");

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/v1/support`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setTickets(await r.json());
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const handleSubmit = async () => {
    if (!subject.trim() || !description.trim()) {
      setError("Please fill in the subject and description.");
      return;
    }
    setSubmitting(true); setError("");
    try {
      const r = await fetch(`${API}/api/v1/support`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subject, category, description, priority }),
      });
      if (r.ok) {
        setSubmitOk(true);
        setSubject(""); setCategory("general"); setDescription(""); setPriority("normal");
        setTimeout(() => { setSubmitOk(false); setShowForm(false); fetchTickets(); }, 2000);
      } else {
        const d = await r.json();
        setError(d.error || "Failed to submit. Please try again.");
      }
    } catch { setError("Connection error. Please try again."); }
    setSubmitting(false);
  };

  const inp = {
    width: "100%", padding: "11px 14px",
    background: "var(--bg-deepest)", border: "1px solid var(--border-strong)",
    borderRadius: "10px", color: "var(--text-primary)", fontSize: "14px",
    fontFamily: "'Capsule Sans', sans-serif", outline: "none",
    boxSizing: "border-box", transition: "border-color 0.2s",
  };

  const openTickets   = tickets.filter(t => t.status === "open" || t.status === "in_progress");
  const closedTickets = tickets.filter(t => t.status === "resolved" || t.status === "closed");

  return (
    <div style={{ padding: "32px", maxWidth: "820px", fontFamily: "'Capsule Sans', sans-serif" }}>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "32px", flexWrap: "wrap", gap: "16px" }}>
        <div>
          <h1 style={{ fontSize: "24px", fontWeight: "800", color: "var(--text-primary)", letterSpacing: "-0.03em", marginBottom: "6px" }}>
            Support
          </h1>
          <p style={{ fontSize: "13px", color: "var(--text-muted)" }}>
            Submit a request and our team will get back to you.
          </p>
        </div>
        <button
          onClick={() => { setShowForm(f => !f); setError(""); setSubmitOk(false); }}
          style={{
            padding: "10px 22px",
            background: showForm ? "transparent" : "linear-gradient(135deg, #D4AF37, #9A7C1E)",
            border: showForm ? "1px solid var(--border-strong)" : "none",
            borderRadius: "10px", color: showForm ? "#b8b4b0" : "#121A12",
            fontSize: "13px", fontWeight: "700", cursor: "pointer",
            fontFamily: "'Capsule Sans', sans-serif",
          }}
        >
          {showForm ? "Cancel" : "+ New Request"}
        </button>
      </div>

      {/* ── New Request Form ────────────────────────────────────────────── */}
      {showForm && (
        <div style={{
          background: "var(--bg-main)", border: "1px solid var(--border-strong)",
          borderRadius: "18px", padding: "28px",
          marginBottom: "32px",
          animation: "fadeSlideIn 0.2s ease",
        }}>
          <style>{`
            @keyframes fadeSlideIn { from { opacity:0; transform: translateY(-8px); } to { opacity:1; transform: none; } }
          `}</style>
          <div style={{ fontSize: "11px", color: "#D4AF37", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.12em", marginBottom: "20px" }}>
            NEW SUPPORT REQUEST
          </div>

          {/* Category */}
          <div style={{ marginBottom: "18px" }}>
            <label style={{ display: "block", fontSize: "11px", color: "var(--text-muted)", letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: "'JetBrains Mono', monospace", marginBottom: "10px" }}>Category</label>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {CATEGORIES.map(c => (
                <button
                  key={c.value}
                  onClick={() => setCategory(c.value)}
                  style={{
                    padding: "8px 16px", borderRadius: "8px", border: "none",
                    background: category === c.value ? "rgba(212,175,55,0.15)" : "var(--bg-card)",
                    color: category === c.value ? "#D4AF37" : "#777",
                    fontSize: "12px", fontWeight: category === c.value ? "600" : "400",
                    cursor: "pointer", fontFamily: "'Capsule Sans', sans-serif",
                    outline: category === c.value ? "1px solid rgba(212,175,55,0.35)" : "1px solid var(--border-strong)",
                    transition: "all 0.15s",
                  }}
                >{c.icon} {c.label}</button>
              ))}
            </div>
          </div>

          {/* Priority */}
          <div style={{ marginBottom: "18px" }}>
            <label style={{ display: "block", fontSize: "11px", color: "var(--text-muted)", letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: "'JetBrains Mono', monospace", marginBottom: "10px" }}>Priority</label>
            <div style={{ display: "flex", gap: "8px" }}>
              {PRIORITIES.map(p => (
                <button
                  key={p.value}
                  onClick={() => setPriority(p.value)}
                  style={{
                    padding: "7px 16px", borderRadius: "8px", border: "none",
                    background: priority === p.value ? "rgba(212,175,55,0.1)" : "var(--bg-card)",
                    color: priority === p.value ? p.color : "#555",
                    fontSize: "12px", cursor: "pointer", fontFamily: "'Capsule Sans', sans-serif",
                    outline: priority === p.value ? `1px solid ${p.color}55` : "1px solid var(--border-strong)",
                    transition: "all 0.15s",
                  }}
                >{p.label}</button>
              ))}
            </div>
          </div>

          {/* Subject */}
          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", fontSize: "11px", color: "var(--text-muted)", letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: "'JetBrains Mono', monospace", marginBottom: "8px" }}>Subject</label>
            <input
              style={inp}
              placeholder="Brief description of your issue"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              onFocus={e => e.target.style.borderColor = "#D4AF37"}
              onBlur={e => e.target.style.borderColor = "var(--border-strong)"}
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", fontSize: "11px", color: "var(--text-muted)", letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: "'JetBrains Mono', monospace", marginBottom: "8px" }}>Description</label>
            <textarea
              rows={5}
              style={{ ...inp, resize: "vertical", lineHeight: "1.6" }}
              placeholder="Describe your issue in as much detail as possible…"
              value={description}
              onChange={e => setDescription(e.target.value)}
              onFocus={e => e.target.style.borderColor = "#D4AF37"}
              onBlur={e => e.target.style.borderColor = "var(--border-strong)"}
            />
          </div>

          {error && (
            <div style={{ background: "#2a1010", border: "1px solid #5a2020", borderRadius: "8px", padding: "10px 14px", fontSize: "12px", color: "#e07070", marginBottom: "16px" }}>
              {error}
            </div>
          )}
          {submitOk && (
            <div style={{ background: "#0e2a18", border: "1px solid #1a5a30", borderRadius: "8px", padding: "10px 14px", fontSize: "12px", color: "#52D68A", marginBottom: "16px" }}>
              ✓ Request submitted! We'll get back to you soon.
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              padding: "12px 28px",
              background: submitting ? "#333" : "linear-gradient(135deg, #D4AF37, #9A7C1E)",
              border: "none", borderRadius: "10px",
              color: submitting ? "#666" : "#121A12",
              fontSize: "14px", fontWeight: "700", cursor: submitting ? "not-allowed" : "pointer",
              fontFamily: "'Capsule Sans', sans-serif", opacity: submitting ? 0.6 : 1,
            }}
          >
            {submitting ? "Submitting…" : "Submit Request →"}
          </button>
        </div>
      )}

      {/* ── Ticket list ─────────────────────────────────────────────────── */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace", fontSize: "12px" }}>
          LOADING…
        </div>
      ) : tickets.length === 0 ? (
        <div style={{
          textAlign: "center", padding: "64px 32px",
          background: "var(--bg-main)", borderRadius: "18px", border: "1px solid var(--border)",
        }}>
          <div style={{ fontSize: "36px", marginBottom: "16px", opacity: 0.4 }}>◈</div>
          <div style={{ fontSize: "16px", fontWeight: "600", color: "var(--text-primary)", marginBottom: "8px" }}>No support requests yet</div>
          <div style={{ fontSize: "13px", color: "var(--text-faint)" }}>Hit "+ New Request" above to open your first ticket.</div>
        </div>
      ) : (
        <>
          {openTickets.length > 0 && (
            <div style={{ marginBottom: "28px" }}>
              <div style={{ fontSize: "11px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", marginBottom: "12px" }}>
                ACTIVE · {openTickets.length}
              </div>
              {openTickets.map(t => (
                <TicketCard
                  key={t.id} ticket={t}
                  expanded={expanded === t.id}
                  onExpand={() => setExpanded(expanded === t.id ? null : t.id)}
                />
              ))}
            </div>
          )}

          {closedTickets.length > 0 && (
            <div>
              <div style={{ fontSize: "11px", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", marginBottom: "12px" }}>
                CLOSED · {closedTickets.length}
              </div>
              {closedTickets.map(t => (
                <TicketCard
                  key={t.id} ticket={t}
                  expanded={expanded === t.id}
                  onExpand={() => setExpanded(expanded === t.id ? null : t.id)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
